import { useState, useEffect } from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Copy } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProcessorEditHeader } from '../../components/processor-edit-header';
import { Button } from '@/components/ui/button';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';
import { useToast } from '@/components/ui/use-toast';
import { SecretInput } from '@/components/form/secret-input';
import { config } from '@/lib/config';

interface NgeniusEditPageProps {
  processor: ProcessorDetails;
}

const NgeniusEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <NgeniusEditPage processor={data} />;
};

const NgeniusEditPage = ({ processor }: NgeniusEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const { toast } = useToast();
  const processorExit = useProcessorEditExit();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    sandboxed: (processor.options as any)?.sandboxed ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    apiKey: (processor.options as any)?.apiKey || '',
    apiUrl: (processor.options as any)?.apiUrl || '',
    outlet: (processor.options as any)?.outlet || '',
    webhookSecret: (processor.options as any)?.webhookSecret || '',
  });

  // Generate webhook secret if not exists
  useEffect(() => {
    if (!formData.webhookSecret) {
      const generateSecret = () => {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
      };
      setFormData(prev => ({ ...prev, webhookSecret: generateSecret() }));
    }
  }, []);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ description: `${label} copied to clipboard` });
  };

  const handleSubmit = async () => {
    setValidationError(null);

    if (!formData.apiKey) {
      setValidationError(t('apiKeyRequired') || 'API Key is required');
      return;
    }

    if (!formData.sandboxed && !formData.apiUrl) {
      setValidationError(t('apiUrlRequired') || 'API URL is required for live mode');
      return;
    }

    if (!formData.outlet) {
      setValidationError(t('outletRequired') || 'Outlet Reference is required');
      return;
    }

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'ngenius',
          name: formData.name,
          enabled: formData.enabled,
          options: {
            sandboxed: formData.sandboxed,
            dryMode: formData.dryMode,
            apiKey: formData.apiKey,
            apiUrl: formData.apiUrl,
            outlet: formData.outlet,
            webhookSecret: formData.webhookSecret,
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('editNgeniusConfiguration') || 'Edit N-Genius Configuration'}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Important Notice */}
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            {t('ngenius.notice3DSUnsupported')}
          </AlertDescription>
        </Alert>

        {/* Sandbox Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('sandboxEnvironment') || 'Sandbox Environment'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sandboxed"
                checked={formData.sandboxed}
                onCheckedChange={(checked) => setFormData({ ...formData, sandboxed: checked === true })}
              />
              <Label htmlFor="sandboxed">{t('enableSandbox') || 'Sandbox environment'}</Label>
            </div>
            {formData.sandboxed && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  {t('ngenius.testMode.warning')}
                </p>
                <a
                  href="https://docs.ngenius-payments.com/reference/sandbox-test-environment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm text-blue-600 underline hover:text-blue-800"
                >
                  {t('ngenius.testMode.viewDocs')} →
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* N-Genius Credentials */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('ngeniusCredentials') || 'N-Genius Credentials'}</CardTitle>
              <a
                href="https://docs.ngenius-payments.com/reference/getting-started-direct"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-blue-600 underline hover:text-blue-800"
              >
                View Documentation
              </a>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-700">
                {t('ngenius.credentials.note')}
              </p>
            </div>

            <div>
              <Label htmlFor="apiUrl">
                {t('apiUrl') || 'API URL'}{!formData.sandboxed && ' *'}
              </Label>
              <Input
                id="apiUrl"
                value={formData.apiUrl}
                onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })}
                placeholder={formData.sandboxed ? 'Not required in sandbox mode' : 'https://...'}
                disabled={formData.sandboxed}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {formData.sandboxed
                  ? t('ngenius.credentials.apiUrlSandboxNote')
                  : t('ngenius.credentials.apiUrlLiveNote')}
              </p>
            </div>

            <div>
              <SecretInput
                label={t('apiKey') || 'API Key'}
                value={formData.apiKey}
                onChange={(value) => setFormData({ ...formData, apiKey: value })}
                placeholder={t('ngenius.credentials.apiKeyPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('ngenius.credentials.apiKeyDescription')}</p>
            </div>

            <div>
              <Label htmlFor="outlet">{t('outletReference') || 'Outlet Reference'} *</Label>
              <Input
                id="outlet"
                value={formData.outlet}
                onChange={(e) => setFormData({ ...formData, outlet: e.target.value })}
                placeholder={t('ngenius.credentials.outletRefPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ngenius.credentials.outletDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Webhook Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>{t('ngenius.webhook.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('ngenius.webhook.urlLabel')}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 mt-1 p-3 bg-muted rounded-md font-mono text-sm break-all">
                  {config.apiBaseUrl}/api/public/webhooks/ngenius
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(`${config.apiBaseUrl}/api/public/webhooks/ngenius`, 'Webhook URL')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ngenius.webhook.urlDescription')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('ngenius.webhook.headerKeyLabel')}</Label>
                <div className="mt-1 p-3 bg-muted rounded-md font-mono text-sm">
                  tagada-secret
                </div>
              </div>
              <div>
                <Label>{t('ngenius.webhook.headerValueLabel')}</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 mt-1 p-3 bg-muted rounded-md font-mono text-sm break-all">
                    {formData.webhookSecret || 'Generating...'}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(formData.webhookSecret, 'Webhook Secret')}
                    disabled={!formData.webhookSecret}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  {t('ngenius.webhook.headerValueDescription')}
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>{t('ngenius.webhook.howToConfigureTitle')}</strong>
              </p>
              <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700 space-y-1">
                <li>{t('ngenius.webhook.step1')}</li>
                <li>{t('ngenius.webhook.step2')}</li>
                <li>{t('ngenius.webhook.step3')}</li>
                <li>{t('ngenius.webhook.step4')}
                  <ul className="list-disc pl-4 mt-1">
                    <li>{t('ngenius.webhook.step4a')}</li>
                    <li>{t('ngenius.webhook.step4b')}</li>
                  </ul>
                </li>
                <li>{t('ngenius.webhook.step5')}</li>
                <li>{t('ngenius.webhook.step6')}
                  <ul className="list-disc pl-4 mt-1">
                    <li>{t('ngenius.webhook.step6a')}</li>
                    <li>{t('ngenius.webhook.step6b')}</li>
                  </ul>
                </li>
              </ol>
              <p className="text-sm text-blue-700 mt-3">
                <strong>{t('ngenius.webhook.note')}</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        <ProcessorDryModeField
          checked={formData.dryMode}
          onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
          disabled={updateProcessorMutation.isPending}
        />
      </div>
    </div>
  );
};

export const NgeniusEditPageContainer = withRequiredPermission(NgeniusEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
